import useCategoryData from "@app/data-hooks/use-category-data.ts";
import useInvoiceManagementData from "@app/data-hooks/use-invoice-management-data.ts";
import { Box, Button, Stack, TextField } from "@mui/material";
import { useForm } from "@tanstack/react-form";
import { Link, useNavigate } from "@tanstack/react-router";
import { useStore } from "@tanstack/react-store";
import { CurrencyInput, QueryStatusRouter, Select } from "bi-lib";

interface FormProps {
    categoryId: number | undefined;
    subcategoryId: number | undefined;
    amount: number | undefined;
    comment: string;
}

interface Props {
    invoiceId: number;
    from?: string;
}

export default function InvoiceAdjustmentForm({ invoiceId, from }: Props) {
    const { createAdjustment } = useInvoiceManagementData();

    const navigate = useNavigate();

    const form = useForm({
        defaultValues: {
            categoryId: 0,
            comment: "",
        } as FormProps,
        onSubmit: async ({ value }) => {
            await createAdjustment.mutateAsync({
                invoiceId,
                subcategoryId: value.subcategoryId,
                amount: value.amount,
                comment: value.comment,
            });

            void navigate({ to: from ?? ".." });
        },
    });

    const categoryId = useStore(form.store, (state) => state.values.categoryId);

    const { categories, subcategoryList } = useCategoryData({
        entryCategoryId: categoryId,
    });

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                void form.handleSubmit();
            }}
        >
            <Stack spacing={1} mt={1}>
                <form.Field name={"categoryId"}>
                    {(field) => (
                        <QueryStatusRouter queryHook={categories}>
                            <Select
                                label={"Category"}
                                value={field.state.value}
                                onChange={(e) => {
                                    field.handleChange(e);
                                    form.setFieldValue("subcategoryId", undefined);
                                }}
                                required
                                options={categories.data?.map((x) => ({
                                    label: x.categoryDescription,
                                    value: x.categoryId,
                                }))}
                            />
                        </QueryStatusRouter>
                    )}
                </form.Field>
                <form.Field name={"subcategoryId"}>
                    {(field) => (
                        <QueryStatusRouter queryHook={subcategoryList}>
                            <Select
                                label={"Subcategory"}
                                value={field.state.value}
                                onChange={(e) => field.handleChange(e)}
                                required
                                disabled={form.state.values.categoryId == undefined}
                                options={subcategoryList.data?.map((x) => ({
                                    label: x.subcategoryDescription,
                                    value: x.subcategoryId,
                                }))}
                            />
                        </QueryStatusRouter>
                    )}
                </form.Field>
                <form.Field name={"amount"}>
                    {(field) => (
                        <CurrencyInput
                            label={"Amount"}
                            value={field.state.value ?? ""}
                            onChange={(e) => field.handleChange(e)}
                            type={"number"}
                            required
                        />
                    )}
                </form.Field>
                <form.Field name={"comment"}>
                    {(field) => (
                        <TextField
                            label={"Comment"}
                            value={field.state.value ?? ""}
                            onChange={(e) => field.handleChange(e.target.value)}
                            multiline
                            minRows={3}
                        />
                    )}
                </form.Field>
                <Box display={"flex"} gap={1}>
                    <Link to={from ?? ".."}>
                        <Button variant={"outlined"}>Cancel</Button>
                    </Link>
                    <Button variant={"contained"} color={"primary"} type={"submit"}>
                        Save
                    </Button>
                </Box>
            </Stack>
        </form>
    );
}
